import { useRef, useState } from 'preact/hooks';
import type { StripeExpressCheckoutElement, StripeExpressCheckoutElementClickEvent, StripeExpressCheckoutElementConfirmEvent } from '@stripe/stripe-js';
import { getPortalBase, type UpgradeQuote } from '@/api/client';
import { getStripe, buildStripeAppearance, walletButtonLayout, onWalletStackChange } from '@/api/stripe-loader';
import { getStripeLocale } from '@/i18n/loader';
import { t, useT } from '@/i18n/t';
import { logError } from '@/sdkConsole';
import { useStripeElementSlot } from '@/components/useStripeElementSlot';
import { useWalletScrollRestore } from '@/components/useWalletScrollRestore';

export interface UpgradeWalletAttempt {
  isCurrent: () => boolean;
  upgradeId: string;
  convertsTrial: boolean;
}

interface Props {
  quote: UpgradeQuote;
  disabled: boolean;
  onBegin: (upgradeId: string) => UpgradeWalletAttempt | null;
  onCollect: (attempt: UpgradeWalletAttempt) => boolean;
  isCurrent: (attempt: UpgradeWalletAttempt) => boolean;
  onPaymentMethod: (attempt: UpgradeWalletAttempt, paymentMethodId: string) => Promise<void>;
  onRelease: (attempt: UpgradeWalletAttempt) => void;
}

function recurringTerms(quote: UpgradeQuote): Parameters<StripeExpressCheckoutElementClickEvent['resolve']>[0] {
  let interval = quote.billingInterval;
  let count = quote.billingIntervalCount;
  if (interval === 'week') {
    interval = 'day';
    count *= 7;
  }
  if (interval !== 'year' && interval !== 'month' && interval !== 'day' && interval !== 'hour' && interval !== 'minute') {
    throw new Error('Unsupported upgrade billing interval');
  }
  return {
    applePay: {
      recurringPaymentRequest: {
        paymentDescription: quote.toProductName,
        managementURL: getPortalBase(),
        regularBilling: {
          label: quote.toProductName,
          amount: quote.newAmountCents,
          recurringPaymentIntervalUnit: interval,
          recurringPaymentIntervalCount: count,
          recurringPaymentStartDate: new Date(quote.renewsAt * 1000),
        },
        billingAgreement: t('upgrade.payment.renewalNote'),
      },
    },
  };
}

export function UpgradeWalletForm(props: Props) {
  useT();
  const callbacks = useRef(props);
  callbacks.current = props;
  const { quote } = props;
  const slotRef = useRef<HTMLDivElement>(null);
  const [availability, setAvailability] = useState<'loading' | 'available' | 'unavailable'>('loading');
  const [error, setError] = useState<string | null>(null);
  const restoreScroll = useWalletScrollRestore();
  const methods = quote.walletPaymentMethods ?? [];
  const applePay = methods.includes('apple_pay');
  const googlePay = methods.includes('google_pay');

  const slotName = useStripeElementSlot(slotRef, async (mountDiv, cancelled) => {
    let element: StripeExpressCheckoutElement | undefined;
    let attempt: UpgradeWalletAttempt | null = null;
    let collecting = false;
    let sheetFinalized = false;
    const failSheet = (event: StripeExpressCheckoutElementConfirmEvent) => {
      if (sheetFinalized) {
        return;
      }
      sheetFinalized = true;
      try {
        event.paymentFailed({ reason: 'fail' });
      } catch (err) {
        logError('upgrade wallet sheet could not be closed', err);
      }
    };
    let disposed = false;
    let destroyed = false;
    let unsubscribe: (() => void) | undefined;
    const destroy = () => {
      if (destroyed) {
        return;
      }
      destroyed = true;
      unsubscribe?.();
      element?.destroy();
    };
    const current = (owned: UpgradeWalletAttempt) => !cancelled() && !disposed && callbacks.current.isCurrent(owned);
    const failLoad = (err: unknown) => {
      logError('upgrade wallet unavailable', err);
      disposed = true;
      if (attempt && !collecting) {
        callbacks.current.onRelease(attempt);
        attempt = null;
      }
      if (!cancelled()) {
        setAvailability('unavailable');
      }
      if (!collecting) {
        destroy();
      }
    };
    try {
      setAvailability('loading');
      const stripe = await getStripe(quote.publishableKey!, quote.providerAccount);
      if (cancelled()) {
        return;
      }
      const elements = stripe.elements({
        mode: 'subscription',
        amount: quote.amountDueCents,
        currency: quote.currency.toLowerCase(),
        paymentMethodCreation: 'manual',
        paymentMethodTypes: ['card'],
        setupFutureUsage: 'off_session',
        appearance: buildStripeAppearance(slotRef.current),
        locale: getStripeLocale(),
      });
      const terms = recurringTerms(quote);
      element = elements.create('expressCheckout', {
        layout: walletButtonLayout(),
        buttonType: { applePay: 'plain', googlePay: 'plain' },
        paymentMethods: {
          applePay: applePay ? 'always' : 'never',
          googlePay: googlePay ? 'always' : 'never',
          link: 'never', paypal: 'never', amazonPay: 'never', klarna: 'never',
        },
      });
      element.on('ready', (event) => {
        if (cancelled() || disposed) {
          return;
        }
        const available = event.availablePaymentMethods;
        setAvailability((applePay && available?.applePay === true) || (googlePay && available?.googlePay === true) ? 'available' : 'unavailable');
      });
      element.on('loaderror', (event) => failLoad(event.error));
      element.on('click', (event) => {
        if (cancelled() || disposed || attempt || callbacks.current.disabled) {
          event.reject();
          return;
        }
        attempt = callbacks.current.onBegin(quote.upgradeId);
        if (!attempt) {
          event.reject();
          return;
        }
        sheetFinalized = false;
        setError(null);
        restoreScroll(event.expressPaymentType);
        try {
          event.resolve(event.expressPaymentType === 'apple_pay' ? terms : {});
        } catch (err) {
          logError('upgrade wallet could not open', err);
          callbacks.current.onRelease(attempt);
          attempt = null;
          setError(t('upgrade.payment.walletFailed'));
        }
      });
      element.on('cancel', () => {
        if (!attempt || collecting) {
          return;
        }
        callbacks.current.onRelease(attempt);
        attempt = null;
      });
      element.on('confirm', async (event) => {
        const owned = attempt;
        if (collecting) {
          return;
        }
        if (!owned || !current(owned) || !callbacks.current.onCollect(owned)) {
          failSheet(event);
          if (owned) {
            callbacks.current.onRelease(owned);
            attempt = null;
          }
          return;
        }
        collecting = true;
        try {
          const submitted = await elements.submit();
          if (!current(owned)) {
            failSheet(event);
            return;
          }
          if (submitted.error) {
            setError(submitted.error.message ?? t('upgrade.payment.walletFailed'));
            failSheet(event);
            return;
          }
          // Stripe finalizes the sheet inside createPaymentMethod, including its errors.
          sheetFinalized = true;
          const created = await stripe.createPaymentMethod({ elements });
          if (!current(owned)) {
            return;
          }
          if (created.error || !created.paymentMethod?.id) {
            setError(created.error?.message ?? t('upgrade.payment.walletFailed'));
            return;
          }
          await callbacks.current.onPaymentMethod(owned, created.paymentMethod.id);
        } catch (err) {
          logError('upgrade wallet authorization failed', err);
          failSheet(event);
          if (current(owned)) {
            setError(t('upgrade.payment.walletFailed'));
          }
        } finally {
          collecting = false;
          callbacks.current.onRelease(owned);
          if (attempt === owned) {
            attempt = null;
          }
          restoreScroll(event.expressPaymentType);
          if (disposed || cancelled()) {
            destroy();
          }
        }
      });
      unsubscribe = onWalletStackChange(() => {
        if (!disposed && !cancelled()) {
          element?.update({ layout: walletButtonLayout() });
        }
      });
      element.mount(mountDiv);
    } catch (err) {
      failLoad(err);
    }
    return () => {
      disposed = true;
      if (attempt) {
        callbacks.current.onRelease(attempt);
      }
      if (!collecting) {
        destroy();
      }
    };
  }, [quote.upgradeId, quote.publishableKey, quote.providerAccount, quote.currency, quote.amountDueCents, quote.newAmountCents, quote.billingInterval, quote.billingIntervalCount, quote.renewsAt, quote.toProductName, applePay, googlePay]);

  return (
    <div class="sm-upgrade-wallet">
      <div ref={slotRef} class={`sm-payment-slot-wallet${availability === 'unavailable' ? ' sm-payment-slot-wallet--empty' : ''}`} aria-disabled={props.disabled}>
        {availability === 'loading' && <div class="sm-wallet-skeleton" aria-hidden="true" />}
        <slot name={slotName} />
      </div>
      {error && <p role="alert" class="sm-error">{error}</p>}
      {availability !== 'unavailable' && <div class="sm-wallet-divider" aria-hidden="true"><span>{t('paymentForm.orPayWithCard')}</span></div>}
    </div>
  );
}
